import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Lock, Mail, Eye, EyeOff, LogIn, ArrowLeft, KeyRound, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Forgot password states
  const [view, setView] = useState('login'); // 'login', 'forgot', 'otp', 'reset'
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.toLowerCase(),
        password
      });
      
      const { access_token, email: userEmail } = response.data;
      
      sessionStorage.setItem('auth_token', access_token);
      sessionStorage.setItem('auth_user', userEmail);
      
      toast.success(`Welcome back!`);
      onLogin(access_token, userEmail);
      
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || 'Login failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: email.toLowerCase()
      });
      
      toast.success('OTP sent to your email!');
      
      // For development without email service
      if (response.data.dev_otp) {
        toast.info(`Development OTP: ${response.data.dev_otp}`, { duration: 10000 });
      }
      
      setView('otp');
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, {
        email: email.toLowerCase(),
        otp
      });
      
      toast.success('OTP verified!');
      setView('reset');
    } catch (error) {
      console.error('OTP verification error:', error);
      const errorMsg = error.response?.data?.detail || 'Invalid OTP';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: email.toLowerCase(),
        otp,
        new_password: newPassword
      });
      
      toast.success('Password reset successfully! Please login.');
      setView('login');
      setPassword('');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reset password';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin} className="p-8 space-y-6">
      <div className="space-y-4">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">
            <Mail size={20} />
          </div>
          <Input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-cyan focus:ring-brand-cyan/20 h-12"
            data-testid="login-email"
          />
        </div>

        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">
            <Lock size={20} />
          </div>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-cyan focus:ring-brand-cyan/20 h-12"
            data-testid="login-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-12 bg-gradient-to-r from-brand-cyan to-brand-blue hover:from-brand-cyan/90 hover:to-brand-blue/90 text-white font-semibold shadow-lg shadow-brand-cyan/25 transition-all"
        data-testid="login-submit"
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Signing in...
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <LogIn size={20} />
            Sign In
          </div>
        )}
      </Button>

      <button
        type="button"
        onClick={() => setView('forgot')}
        className="w-full text-center text-white/60 hover:text-white text-sm transition-colors"
      >
        Forgot Password?
      </button>

      <p className="text-center text-white/40 text-xs mt-4">
        Protected admin area. Authorized personnel only.
      </p>
    </form>
  );

  const renderForgotForm = () => (
    <form onSubmit={handleForgotPassword} className="p-8 space-y-6">
      <button
        type="button"
        onClick={() => setView('login')}
        className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors mb-4"
      >
        <ArrowLeft size={16} />
        Back to Login
      </button>

      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-white mb-2">Forgot Password?</h2>
        <p className="text-white/60 text-sm">Enter your email to receive an OTP</p>
      </div>

      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">
          <Mail size={20} />
        </div>
        <Input
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-cyan focus:ring-brand-cyan/20 h-12"
          data-testid="forgot-email"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-12 bg-gradient-to-r from-brand-orange to-brand-yellow hover:from-brand-orange/90 hover:to-brand-yellow/90 text-white font-semibold shadow-lg transition-all"
        data-testid="forgot-submit"
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Sending OTP...
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Mail size={20} />
            Send OTP
          </div>
        )}
      </Button>
    </form>
  );

  const renderOTPForm = () => (
    <form onSubmit={handleVerifyOTP} className="p-8 space-y-6">
      <button
        type="button"
        onClick={() => setView('forgot')}
        className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors mb-4"
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-white mb-2">Enter OTP</h2>
        <p className="text-white/60 text-sm">We sent a 6-digit code to {email}</p>
      </div>

      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">
          <KeyRound size={20} />
        </div>
        <Input
          type="text"
          placeholder="Enter 6-digit OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-cyan focus:ring-brand-cyan/20 h-12 text-center text-xl tracking-widest"
          maxLength={6}
          data-testid="otp-input"
        />
      </div>

      <Button
        type="submit"
        disabled={loading || otp.length !== 6}
        className="w-full h-12 bg-gradient-to-r from-brand-cyan to-brand-blue hover:from-brand-cyan/90 hover:to-brand-blue/90 text-white font-semibold shadow-lg transition-all"
        data-testid="otp-submit"
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Verifying...
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <CheckCircle size={20} />
            Verify OTP
          </div>
        )}
      </Button>

      <button
        type="button"
        onClick={handleForgotPassword}
        className="w-full text-center text-white/60 hover:text-white text-sm transition-colors"
      >
        Resend OTP
      </button>
    </form>
  );

  const renderResetForm = () => (
    <form onSubmit={handleResetPassword} className="p-8 space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-4">
          <CheckCircle size={32} className="text-green-400" />
        </div>
        <h2 className="text-xl font-semibold text-white mb-2">Create New Password</h2>
        <p className="text-white/60 text-sm">Enter your new password below</p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">
            <Lock size={20} />
          </div>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-cyan focus:ring-brand-cyan/20 h-12"
            data-testid="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">
            <Lock size={20} />
          </div>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-cyan focus:ring-brand-cyan/20 h-12"
            data-testid="confirm-password"
          />
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-12 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-500/90 hover:to-green-600/90 text-white font-semibold shadow-lg transition-all"
        data-testid="reset-submit"
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Resetting...
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <CheckCircle size={20} />
            Reset Password
          </div>
        )}
      </Button>
    </form>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-blue to-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-cyan/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-magenta/20 rounded-full blur-3xl"></div>
      </div>

      <Card className="relative w-full max-w-md bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl rounded-2xl overflow-hidden" data-testid="login-card">
        <div className="bg-gradient-to-r from-brand-cyan to-brand-blue p-8 text-center">
          <div className="w-20 h-20 mx-auto bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mb-4 shadow-lg">
            <img src="/saps-logo.png" alt="SAPS" className="w-12 h-12" onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">SAPS Admin</h1>
          <p className="text-white/70 text-sm">Skills and Placement Services</p>
        </div>

        {view === 'login' && renderLoginForm()}
        {view === 'forgot' && renderForgotForm()}
        {view === 'otp' && renderOTPForm()}
        {view === 'reset' && renderResetForm()}
      </Card>
    </div>
  );
};

export default Login;
